import { type ColumnDef } from "@tanstack/react-table";
import {
  Badge,
  Button,
  DataTableColumnHeader,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@repo/ui";
import { Eye } from "lucide-react";
import type { ActivityTransaction } from "@/lib/types/activity";
import { NdcLink } from "@/components/ndc-link";
import { NotesCell } from "@/components/notes-cell";
import { DateTimeCell } from "@/components/date-time-cell";
import { multiValueFilterFn } from "@/lib/utils/inventory-format";

export function createActivityColumns(
  onView: (transaction: ActivityTransaction) => void,
  onNavigateToInventory: (ndc: string) => void,
  onNavigateToTransaction: (
    transactionType: string,
    transactionId: string
  ) => void,
  onNDCClick?: (ndc: string) => void,
  onEditNotes?: (transaction: ActivityTransaction) => void
): ColumnDef<ActivityTransaction>[] {
  return [
    {
      accessorKey: "transaction_date",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Date & Time" />
      ),
      cell: ({ row }) => {
        const date = new Date(row.getValue("transaction_date"));
        return <DateTimeCell date={date} time={date} />;
      },
      enableSorting: true,
    },
    {
      accessorKey: "transaction_type",
      header: () => <div className="text-center">Type</div>,
      cell: ({ row }) => {
        const type = row.getValue("transaction_type") as string;
        const colorMap = {
          receipt: "bg-surface font-semibold text-emerald-600 border-border",
          dispense: "bg-surface font-semibold text-foreground border-border",
          adjustment: "bg-surface font-semibold text-amber-600 border-border",
        };
        return (
          <div className="text-center">
            <Badge
              variant="outline"
              className={colorMap[type as keyof typeof colorMap]}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </Badge>
          </div>
        );
      },
      filterFn: multiValueFilterFn,
    },
    {
      accessorKey: "item_name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Drug" />
      ),
      cell: ({ row }) => {
        const itemName = row.getValue("item_name") as string;
        const ndc = row.original.ndc;
        return (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => onNavigateToInventory(ndc)}
                  className="text-left hover:underline hover:text-primary transition-colors"
                >
                  <div className="font-medium max-w-[180px] truncate">
                    {itemName}
                  </div>
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{itemName}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: "ndc",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="NDC" />
      ),
      cell: ({ row }) => (
        <NdcLink ndc={row.original.ndc} onClick={onNDCClick} />
      ),
    },
    {
      accessorKey: "quantity",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Quantity"
          className="justify-center"
        />
      ),
      cell: ({ row }) => {
        const quantity = row.getValue("quantity") as number;
        const isPositive = quantity > 0;
        return (
          <div
            className={`text-center font-semibold ${
              isPositive ? "text-emerald-600" : "text-foreground"
            }`}
          >
            {isPositive ? "+" : ""}
            {quantity}
          </div>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: "stock_after_transaction",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Stock After"
          className="justify-center"
        />
      ),
      cell: ({ row }) => {
        const stock = row.getValue("stock_after_transaction") as number | null;
        return (
          <div className="text-center font-medium text-sm">
            {stock !== null ? stock : "—"}
          </div>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: "details",
      header: "Reference",
      cell: ({ row }) => {
        const details = row.original.details;
        const type = row.original.transaction_type;

        const adjustmentReasonMap: Record<string, string> = {
          physical_count: "Physical Count",
          breakage: "Breakage/Damage",
          expiration: "Expiration",
          theft_loss: "Theft/Loss",
          data_entry_error: "Data Entry Error",
          other: "Other",
        };

        let referenceNumber = "";
        if (type === "receipt" && details.invoice_number) {
          referenceNumber = `INV: ${details.invoice_number}`;
        } else if (type === "dispense" && details.rx_number) {
          referenceNumber = `RX: ${details.rx_number}`;
        } else if (type === "adjustment" && details.reason) {
          referenceNumber =
            adjustmentReasonMap[details.reason] || details.reason;
        }

        return referenceNumber ? (
          <span className="text-sm text-muted-foreground truncate max-w-[120px] block">
            {referenceNumber}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        );
      },
    },
    {
      accessorKey: "created_by_name",
      header: "Created By",
      cell: ({ row }) => {
        const createdBy = row.getValue("created_by_name") as string;
        return createdBy ? (
          <span className="text-sm">{createdBy}</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        );
      },
    },
    {
      id: "notes",
      header: () => <div className="text-center">Notes</div>,
      cell: ({ row }) => {
        const notes = row.original.details?.notes;
        const hasNotes = Array.isArray(notes) && notes.length > 0;
        return (
          <NotesCell
            hasNotes={hasNotes}
            onClickNotes={() => onEditNotes?.(row.original)}
          />
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const transaction = row.original;
        const type = transaction.transaction_type;
        const transactionId = transaction.transaction_id;

        const handleNavigate = () => {
          if (type === "adjustment") {
            onView(transaction);
          } else {
            onNavigateToTransaction(type, transactionId);
          }
        };

        return (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleNavigate}
              className="h-8 w-8"
            >
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];
}
